import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
export function useDebounce(initialValue, timeout) {
    const [value, setActualValue] = useState(initialValue);
    const timeoutId = useRef(0);
    const setValue = useCallback((value, force = false) => {
        if (force) {
            setActualValue(value);
            return;
        }
        const hasTimeout = !!timeoutId.current;
        if (timeoutId.current) {
            clearInterval(timeoutId.current);
        }
        timeoutId.current = window.setTimeout(() => {
            timeoutId.current = 0;
        }, timeout);
        if (hasTimeout) {
            return;
        }
        setActualValue(value);
    }, 
    // if timeout changes, we don't want to create a new callback. (only initial value is used)
    [] // eslint-disable-line react-hooks/exhaustive-deps
    );
    return [value, setValue];
}
export function useContextMenuHandlers(onReportContextMenu) {
    const refOnReportContextMenu = useRef(onReportContextMenu);
    useEffect(() => {
        refOnReportContextMenu.current = onReportContextMenu;
    }, [onReportContextMenu]);
    return useMemo(() => {
        const onKeyDown = (e) => {
            if (refOnReportContextMenu.current &&
                (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey))) {
                e.preventDefault();
                const targetElement = e.target instanceof HTMLElement ? e.target : document.activeElement;
                const { x = 0, y = 0 } = (targetElement === null || targetElement === void 0 ? void 0 : targetElement.getBoundingClientRect()) || {};
                refOnReportContextMenu.current({
                    x,
                    y,
                    source: 'keyboard',
                    nativeEvent: e.nativeEvent,
                });
            }
        };
        const onContextMenu = (e) => {
            if (refOnReportContextMenu.current) {
                e.preventDefault();
                refOnReportContextMenu.current({
                    x: e.pageX,
                    y: e.pageY,
                    source: 'mouse',
                    nativeEvent: e.nativeEvent,
                });
            }
        };
        return { onKeyDown, onContextMenu };
    }, []);
}
export function useLatestRef(value) {
    const ref = useRef(value);
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref;
}
export function useTimeout(callback, timeout) {
    const refCallback = useLatestRef(callback);
    useEffect(() => {
        const timeoutId = setTimeout(() => refCallback.current(), timeout);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [timeout, refCallback]);
}
export function useOnUnmount(onUnmount) {
    const refOnUnmount = useLatestRef(onUnmount);
    useEffect(() => () => refOnUnmount.current(), [refOnUnmount]);
}
export function useMinPanelHeight(headerRef, footerRef, callback) {
    useEffect(() => {
        var _a, _b;
        const minPanelHeight = (((_a = headerRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 28) /* defaults to estimated height */ +
            (((_b = footerRef.current) === null || _b === void 0 ? void 0 : _b.clientHeight) || 61) +
            16 /* padding-top above header  */ +
            3; /* footer divider & border pixels */
        callback(minPanelHeight);
    }, [headerRef, footerRef, callback]);
}
